import { Link } from 'react-router-dom'
import DocsSidebar from '../components/DocsSidebar'
import Footer from '../components/Footer'
import SEO from '../components/SEO'
import SiteNav from '../components/SiteNav'
import '../styles/docs.css'

const QUICK_STARTS = [
  'Deploy this project with OpenDeploy.',
  '/opendeploy',
  '/opendeploy show logs',
  '/opendeploy add Postgres',
]

const CAPABILITIES = [
  {
    intent: 'Deploy or preview a project',
    ask: 'Deploy this project, host this, ship this, get a live URL',
    result: 'The agent inspects the app, deploys it to OpenDeploy, watches the build, and returns the live URL.',
    skill: 'opendeploy',
  },
  {
    intent: 'Use the short deploy command',
    ask: '/deploy',
    result: 'Same deploy flow as /opendeploy. It is only a shortcut.',
    skill: 'deploy',
  },
  {
    intent: 'Install, update, or repair OpenDeploy',
    ask: 'Install OpenDeploy, update the CLI, run doctor, check version',
    result: 'The agent checks the local CLI and plugin, asks before updates, and verifies the setup.',
    skill: 'opendeploy-setup',
  },
  {
    intent: 'Log in or connect an account',
    ask: 'Log in, auth status, bind account, use this token',
    result: 'The agent checks credentials, handles guest deploy credentials, and helps with account binding.',
    skill: 'opendeploy-auth',
  },
  {
    intent: 'Reuse an existing project or service',
    ask: 'Use the same service, what project is this, avoid duplicate project',
    result: 'The agent resolves saved project, service, and deployment IDs for the current directory.',
    skill: 'opendeploy-context',
  },
  {
    intent: 'Fix build, start, port, or resource settings',
    ask: 'Set build command, change start command, use this Dockerfile, change port',
    result: 'The agent reads and updates service configuration such as root dir, builder, Dockerfile, port, CPU, memory, and healthcheck.',
    skill: 'opendeploy-config',
  },
  {
    intent: 'Manage environment variables',
    ask: 'Sync .env, set env vars, rotate secrets, remove this key',
    result: 'The agent scans keys, asks before uploading secrets, and patches service environment variables.',
    skill: 'opendeploy-env',
  },
  {
    intent: 'Add a database or cache',
    ask: 'Add Postgres, create Redis, fix DATABASE_URL, rotate database password',
    result: 'The agent plans and manages OpenDeploy Postgres, MySQL, MongoDB, or Redis dependencies.',
    skill: 'opendeploy-database',
  },
  {
    intent: 'Deploy a monorepo or multi-service app',
    ask: 'Deploy this monorepo, deploy web plus worker, use docker-compose',
    result: 'The agent plans service splits, app roots, workers, cron jobs, dependencies, and ports.',
    skill: 'opendeploy-monorepo',
  },
  {
    intent: 'Use a custom domain',
    ask: 'Bind my domain, rename subdomain, check DNS, verify SSL',
    result: 'The agent manages OpenDeploy subdomains, custom domains, CNAME checks, and SSL verification.',
    skill: 'opendeploy-domain',
  },
  {
    intent: 'Debug a failed or unreachable app',
    ask: 'Show logs, why did deploy fail, fix 502, wrong port, DB not ready',
    result: 'The agent reads build/runtime logs, checks health, and routes to the right fix.',
    skill: 'opendeploy-debug',
  },
  {
    intent: 'Operate a live service',
    ask: 'Check health, restart, stop, start, rollback, resize, retry deployment',
    result: 'The agent inspects live service state and asks before mutations such as restart or rollback.',
    skill: 'opendeploy-ops',
  },
  {
    intent: 'Manage persistent storage',
    ask: 'Attach a volume, persist uploads, persist SQLite, resize disk',
    result: 'The agent manages OpenDeploy persistent volumes and explains downtime or storage limits before changes.',
    skill: 'opendeploy-volume',
  },
  {
    intent: 'Handle alarms and support',
    ask: 'Acknowledge alarm, resolve alarm, contact OpenDeploy support, open Discord support',
    result: 'The agent manages alarm state or gets the private OpenDeploy support channel when help is needed.',
    skill: 'opendeploy-alarms / opendeploy-oncall',
  },
  {
    intent: 'Use an advanced API path',
    ask: 'CLI lacks this route, call the OpenDeploy API',
    result: 'The agent uses the API only when the CLI does not expose the needed route. Deletes stay dashboard-only.',
    skill: 'opendeploy-api',
  },
]

export default function OpendeployCapabilitiesDocs() {
  return (
    <>
      <SEO
        title="What /opendeploy Can Do"
        description="A simple guide to the OpenDeploy agent skills behind /opendeploy."
        path="/docs/what-opendeploy-can-do"
      />
      <div className="od-docs-page">
        <SiteNav />
        <div className="od-docs-frame od-docs-frame--article">
          <DocsSidebar />

          <main className="od-docs-article">
            <nav className="od-docs-breadcrumb" aria-label="Breadcrumb">
              <Link to="/docs">Docs</Link>
              <span>/</span>
              <span>What /opendeploy can do</span>
            </nav>

            <header id="overview" className="od-docs-article-hero">
              <p className="od-docs-kicker">Agent deployment</p>
              <h1>What /opendeploy can do</h1>
              <p>
                Use /opendeploy when you want your coding agent to deploy, configure, debug,
                or operate an app on OpenDeploy. You usually do not need to choose a specific
                skill; the agent routes your request to the right OpenDeploy helper.
              </p>
              <div className="od-docs-meta">
                <span>Updated May 14, 2026</span>
                <span>{CAPABILITIES.length} request types</span>
                <span>/deploy is a shortcut</span>
              </div>
            </header>

            <section id="examples" className="od-docs-article-section">
              <div className="od-docs-section-copy">
                <p className="od-docs-kicker">Examples</p>
                <h2>Start with plain language</h2>
                <p>Say what you want done. OpenDeploy handles the platform details behind the scenes.</p>
              </div>

              <div className="od-docs-install-card">
                <div className="od-docs-install-commands" role="list">
                  {QUICK_STARTS.map((command, index) => (
                    <div className="od-docs-install-command-row" role="listitem" key={command}>
                      <span className="od-docs-install-command-index" aria-hidden="true">
                        {index + 1}
                      </span>
                      <code>{command}</code>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section id="skills" className="od-docs-article-section">
              <div className="od-docs-section-copy">
                <p className="od-docs-kicker">Skill map</p>
                <h2>What to ask for</h2>
                <p>These are the OpenDeploy skills your agent uses. The public command stays simple: start with /opendeploy.</p>
              </div>

              <div className="od-docs-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>When you need</th>
                      <th>Ask your agent</th>
                      <th>What happens</th>
                      <th>Skill</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CAPABILITIES.map((item) => (
                      <tr key={item.skill}>
                        <td>{item.intent}</td>
                        <td>{item.ask}</td>
                        <td>{item.result}</td>
                        <td>{item.skill}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </main>

          <aside className="od-docs-toc" aria-label="On this page">
            <p>On this page</p>
            <a href="#overview">Overview</a>
            <a href="#examples">Examples</a>
            <a href="#skills">Skill map</a>
          </aside>
        </div>
        <Footer />
      </div>
    </>
  )
}
